import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Tag, Space } from 'antd';
import { userService, ManagedUser, UserRole, ROLE_LABELS } from '../../services/userService';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      message.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'agent' });
    setIsModalVisible(true);
  };

  const handleEdit = (user: ManagedUser) => {
    setEditingUser(user);
    form.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      password: undefined,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (user: ManagedUser) => {
    Modal.confirm({
      title: 'Supprimer cet utilisateur ?',
      content: `${user.firstName} ${user.lastName} (${user.email})`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await userService.delete(user._id);
          message.success('Utilisateur supprimé');
          fetchUsers();
        } catch (error: any) {
          message.error(error?.response?.data?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        role: values.role as UserRole,
        password: values.password || undefined,
      };

      if (editingUser) {
        await userService.update(editingUser._id, payload);
        message.success('Utilisateur mis à jour');
      } else {
        if (!values.password) {
          message.error('Le mot de passe est requis');
          return;
        }
        await userService.create(payload);
        message.success('Utilisateur créé');
      }
      setIsModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const roleColor = (role: UserRole) => {
    if (role === 'admin') return 'red';
    if (role === 'livreur') return 'blue';
    return 'green';
  };

  const columns = [
    {
      title: 'Nom',
      key: 'name',
      render: (_: any, record: ManagedUser) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Type',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => <Tag color={roleColor(role)}>{ROLE_LABELS[role] || role}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ManagedUser) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            Modifier
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }}>
        Ajouter un utilisateur
      </Button>
      <Table dataSource={users} columns={columns} rowKey="_id" loading={loading} />

      <Modal
        title={editingUser ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okText={editingUser ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="firstName"
            label="Prénom"
            rules={[{ required: true, message: 'Prénom requis' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Nom"
            rules={[{ required: true, message: 'Nom requis' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email requis' },
              { type: 'email', message: 'Email invalide' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="Type d'agent"
            rules={[{ required: true, message: 'Type requis' }]}
          >
            <Select>
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="agent">Agent</Select.Option>
              <Select.Option value="livreur">Livreur</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="password"
            label={editingUser ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}
            rules={
              editingUser
                ? [{ min: 6, message: 'Minimum 6 caractères' }]
                : [
                    { required: true, message: 'Mot de passe requis' },
                    { min: 6, message: 'Minimum 6 caractères' },
                  ]
            }
          >
            <Input.Password placeholder={editingUser ? 'Laisser vide pour ne pas changer' : ''} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
